import { Feather } from '@expo/vector-icons';
import React from 'react';
import {
  ActivityIndicator,
  Modal,
  ModalProps,
  Platform,
  PlatformIOSStatic,
  useWindowDimensions
} from 'react-native';
import styled from 'styled-components/native';
import Button from '../../../components/Button';
import IconButton from '../../../components/IconButton';
import Link from '../../../components/Link';
import { B, H3, H5 } from '../../../components/Typography';
import Colors from '../../../constants/Colors';
import { usePasswordRecover } from '../../../hooks/user/usePasswordRecover';

const StyledDimContainer = styled.View`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
`;

const StyledDialog = styled.View<{ isMobile: boolean }>`
  width: ${p => (p.isMobile ? 100 : 90)}%;
  height: ${p => (p.isMobile ? 100 : 90)}%;
  border-radius: 10px;
  max-width: 1080px;
  max-height: ${p => (p.isMobile ? '100%' : '627px')};
  overflow: hidden;
  background-color: ${Colors.background};
`;

const StyledContentView = styled.KeyboardAvoidingView`
  flex: 1;
`;

const StyledImageBackground = styled.Image<{ isDesktop: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${p => (p.isDesktop ? '100%' : '130%')};
  height: 100%;
  max-height: 100%;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'ios' ? 48 : 20}px;
  left: 20px;
  z-index: 1;
`;

const StyledContainerView = styled.View<{ isDesktop: boolean }>`
  flex: 1;
  flex-direction: ${p => (p.isDesktop ? 'row' : 'column')};
`;

const StyledContainerColumn = styled.View<{ isDesktop: boolean }>`
  flex: ${p => (p.isDesktop ? 1 : '0 1 auto')};
  align-items: center;
`;

const StyledView = styled.View`
  flex: 1;
`;

const StyledHeaderView = styled.View<{ isDesktop: boolean }>`
  padding: ${p =>
    p.isDesktop
      ? '124px 0 0 0'
      : `${Platform.OS === 'ios' ? 110 : 90}px 24px 0 24px`};
  max-width: ${p => (p.isDesktop ? 320 : 430)}px;
  width: 100%;
`;

const StyledButtonContainer = styled.View<{ isDesktop: boolean }>`
  margin-top: ${p => (p.isDesktop ? 180 : 40)}px;
  align-self: center;
  width: 236px;
`;

const StyledFooterContainer = styled.View`
  margin: 0 32px 0 32px;
  align-self: center;
  position: absolute;
  bottom: 40px;
`;

const StyledB = styled(B)`
  font-size: 14px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  font-size: 14px;
  color: ${Colors.primaryPurple};
  text-decoration-line: none;
`;

export type ResetPasswordSentModalProps = ModalProps & {
  email: string;
  onBackToLogin: () => void;
};

const ResetPasswordSentModal: React.FC<ResetPasswordSentModalProps> = ({
  email,
  onBackToLogin,
  ...props
}) => {
  const { onRequestClose } = props;
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const isMobile = window.width < 468;
  const platformIOS = Platform as PlatformIOSStatic;
  const backgroundAsset = isMobile
    ? require('../../../assets/intro/intro-background-start.png')
    : require('../../../assets/intro/intro-background-whole.png');

  const { isLoading, doPasswordRecover } = usePasswordRecover();

  const handleSubmit = () => {
    doPasswordRecover(email);
  };

  return (
    <Modal animationType='fade' transparent {...props}>
      <StyledDimContainer>
        <StyledDialog isMobile={isMobile}>
          <StyledImageBackground
            isDesktop={isDesktop}
            resizeMode='cover'
            source={backgroundAsset}
          />
          <StyledContentView
            keyboardVerticalOffset={Platform.select({
              ios: platformIOS.isPad
                ? window.height >= 1024 && window.height <= 1194
                  ? window.height * 0.23
                  : window.height * 0.27
                : 0
            })}
            behavior={Platform.select({ ios: 'padding' })}
          >
            <StyledIconButton onPress={event => onRequestClose?.(event)}>
              <Feather
                name='arrow-left'
                size={28}
                color={Colors.primaryPurple}
              />
            </StyledIconButton>
            <StyledContainerView isDesktop={isDesktop}>
              <StyledContainerColumn isDesktop={isDesktop}>
                <StyledHeaderView isDesktop={isDesktop}>
                  <H3 color='medium' padding={2}>
                    Email Sent
                  </H3>
                  <H5>
                    Please check your email. We sent a link to reset your
                    password.
                  </H5>
                </StyledHeaderView>
              </StyledContainerColumn>
              <StyledView>
                <StyledButtonContainer isDesktop={isDesktop}>
                  <Button
                    onPress={() => handleSubmit()}
                    disabled={isLoading}
                    endIcon={
                      isLoading && (
                        <ActivityIndicator size='small' color='white' />
                      )
                    }
                  >
                    Resend Email
                  </Button>
                  <Button
                    style={{ marginTop: 20 }}
                    inverse
                    onPress={() => onBackToLogin()}
                  >
                    Back to Login
                  </Button>
                </StyledButtonContainer>
                <StyledFooterContainer>
                  <StyledB color='light'>
                    Didn&apos;t receive an email? Check your spam folder or try{' '}
                    <StyledLink onPress={event => onRequestClose?.(event)}>
                      another email address
                    </StyledLink>
                  </StyledB>
                </StyledFooterContainer>
              </StyledView>
            </StyledContainerView>
          </StyledContentView>
        </StyledDialog>
      </StyledDimContainer>
    </Modal>
  );
};

export default ResetPasswordSentModal;
